import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import { useSelectStyles } from '../utils/selectStyles';

// Local cache for distinct values
const distinctValuesCache = new Map();

// Editable fields configuration
const FIELD_CONFIG = {
  translatedquestion: { labelKey: 'Question', key: 'translatedquestion' },
  item: { labelKey: 'Item', key: 'item' },
  campaign: { labelKey: 'Campaign', key: 'campaign' },
  visittype: { labelKey: 'Visit type', key: 'visittype' }
};

export default function ImageDetailsEditor({ image, fetchWithAuth, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [editFormData, setEditFormData] = useState({});
  const [editOptions, setEditOptions] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const selectStyles = useSelectStyles({});

  //map values count to display type
  const getFieldDisplayType = (values) => {
    if (!values || values.length === 0) return 'hidden';
    if (values.length === 1) return 'disabled';
    return 'select';
  };

  // Fetch distinct options with cache
  const fetchDistinctValues = useCallback(async (column, projectId) => {
    const cacheKey = `${projectId}_${column}`;
    const cached = distinctValuesCache.get(cacheKey);
    if (cached) return cached;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/columns/${column}/distinct/${projectId}`);
      if (!response.ok) throw new Error(`Failed to fetch ${column}`);
      const data = await response.json();
      const values = data.values || [];
      distinctValuesCache.set(cacheKey, values);
      return values;
    } catch (error) {
      console.error(`Error fetching distinct ${column}:`, error);
      return [];
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    let isMounted = true;
    if (!image?.projectid) return;

    (async () => {
      setEditLoading(true);
      try {
        const keys = Object.keys(FIELD_CONFIG);
        const results = await Promise.all(keys.map(k => fetchDistinctValues(k, image.projectid)));

        if (!isMounted) return;
        const options = {};
        keys.forEach((k, i) => {
          options[k] = results[i].map(v => ({ value: v, label: v }));
        });
        setEditOptions(options);

        const form = {};
        keys.forEach(k => {
          const value = image[k];
          form[k] = value ? { value, label: value } : null;
        });
        setEditFormData(form);
      } catch (e) {
        console.error('Error loading edit options:', e);
      } finally {
        if (isMounted) setEditLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [image, fetchDistinctValues]);

  // Save handler
  const handleSave = async () => {
    try {
      const updatedData = {};
      Object.entries(editFormData).forEach(([key, value]) => {
        updatedData[key] = value ? value.value : null;
      });

      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ picture: updatedData })
      });
      if (!response.ok) throw new Error(t('Error saving'));

      const updatedImage = { ...image, ...updatedData };
      if (onSaved) onSaved(updatedImage);
    } catch (error) {
      console.error('Error saving edit:', error);
      alert(t('Error saving'));
    }
  };

  return (
    <div className="border-t border-white/20 mt-2 pt-2">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-300">{t('Edit information')}</h4>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-white hover:bg-opacity-20"
            title={t('Cancel')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            onClick={handleSave}
            className="p-1 text-green-400 hover:text-green-300 rounded-full hover:bg-white hover:bg-opacity-20"
            title={t('Save')}
            disabled={editLoading}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>
      </div>

      {editLoading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-7 bg-white bg-opacity-20 rounded"></div>
          <div className="h-7 bg-white bg-opacity-20 rounded"></div>
          <div className="h-7 bg-white bg-opacity-20 rounded"></div>
          <div className="h-7 bg-white bg-opacity-20 rounded"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(FIELD_CONFIG).map(([fieldKey, config]) => {
            const values = editOptions[fieldKey] || [];
            const displayType = getFieldDisplayType(values);
            if (displayType === 'hidden') return null;
            return (
              <div key={fieldKey}>
                <label className="block text-xs text-gray-300 mb-1">
                  {t(config.labelKey)}
                </label>
                {displayType === 'disabled' ? (
                  <div className="h-7 px-2 py-1 bg-white bg-opacity-10 border border-white border-opacity-20 rounded text-xs text-white flex items-center">
                    {values[0]?.label || t('No value')}
                  </div>
                ) : (
                  <Select
                    value={editFormData[fieldKey]}
                    onChange={(value) => setEditFormData(prev => ({ ...prev, [fieldKey]: value }))}
                    options={values}
                    placeholder={t('Select...')}
                    isClearable
                    className="text-xs"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    menuPlacement="top"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
